import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  getMetaAdsPixelSelection, getMetaAdsPixels, selectMetaAdsPixel,
  type MetaAdsSelectedAdAccount, type MetaAdsPixel, type MetaAdsPixelSelectionResponse,
} from '../services/adCampaignApi.ts';
import { getDashboardThemeClasses } from './dashboard/dashboardTheme.ts';
import MetaCrmSettings from './MetaCrmSettings.tsx';

interface Props {
  clinicId: number;
  accounts: MetaAdsSelectedAdAccount[];
  isDark: boolean;
  footerTarget?: HTMLElement | null;
  onDetailOpenChange?: (open: boolean) => void;
}

// A troca de clínica ou de contas descarta candidatos do contexto anterior.
export default function MetaAdsPixelSettings(props: Props) {
  const scope = `${props.clinicId}:${props.accounts.map(account => account.id).join(',')}`;
  return <PixelSettings key={scope} {...props} />;
}

function PixelSettings({ clinicId, accounts, isDark, footerTarget, onDetailOpenChange }: Props) {
  const theme = getDashboardThemeClasses(isDark);
  const [selection, setSelection] = useState<MetaAdsPixelSelectionResponse | null>(null);
  const [accountId, setAccountId] = useState('');
  const [pixelId, setPixelId] = useState('');
  const [pixels, setPixels] = useState<MetaAdsPixel[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initialError, setInitialError] = useState(false);
  const [listError, setListError] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saved, setSaved] = useState(false);
  const [initialAttempt, setInitialAttempt] = useState(0);
  const [listAttempt, setListAttempt] = useState(0);
  const active = useRef(true);
  const requestVersion = useRef(0);
  const candidateRef = useRef('');
  const selectionRef = useRef<MetaAdsPixelSelectionResponse | null>(null);

  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setInitialLoading(true);
    setInitialError(false);
    getMetaAdsPixelSelection(clinicId, controller.signal).then(result => {
      if (controller.signal.aborted) return;
      selectionRef.current = result;
      setSelection(result);
      const savedAccount = result.selection?.adAccountId;
      setAccountId(accounts.some(account => account.id === savedAccount) ? savedAccount! : accounts[0]?.id || '');
    }).catch(() => {
      if (!controller.signal.aborted) setInitialError(true);
    }).finally(() => {
      if (!controller.signal.aborted) setInitialLoading(false);
    });
    return () => controller.abort();
    // O conjunto de contas é fixo dentro deste contexto identificado pela key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, initialAttempt]);

  useEffect(() => {
    if (!accountId) return;
    const controller = new AbortController();
    const version = ++requestVersion.current;
    setPixels([]);
    setPixelId('');
    setListLoading(true);
    setListError(false);
    setSaved(false);
    setSaveError(false);
    getMetaAdsPixels(clinicId, accountId, controller.signal).then(result => {
      if (controller.signal.aborted || version !== requestVersion.current) return;
      if (result.adAccountId !== accountId) throw new Error('Conta divergente');
      setPixels(result.pixels);
      const previous = selectionRef.current?.selection;
      if (result.pixels.some(pixel => pixel.id === candidateRef.current)) {
        setPixelId(candidateRef.current);
      } else if (!candidateRef.current && previous?.adAccountId === accountId && result.pixels.some(pixel => pixel.id === previous.pixelId)) {
        setPixelId(previous.pixelId);
      }
    }).catch(() => {
      if (!controller.signal.aborted && version === requestVersion.current) setListError(true);
    }).finally(() => {
      if (!controller.signal.aborted && version === requestVersion.current) setListLoading(false);
    });
    return () => controller.abort();
  }, [clinicId, accountId, listAttempt]);

  const candidate = pixels.find(pixel => pixel.id === pixelId);
  const busy = initialLoading || listLoading || saving;
  const save = async () => {
    if (busy || initialError || listError || !candidate || !accounts.some(account => account.id === accountId)) return;
    const version = requestVersion.current;
    setSaving(true);
    setSaveError(false);
    setSaved(false);
    try {
      const result = await selectMetaAdsPixel(clinicId, accountId, candidate.id);
      if (!active.current || requestVersion.current !== version) return;
      const updated: MetaAdsPixelSelectionResponse = { ...result, status: 'readable', scopes: [] };
      selectionRef.current = updated;
      setSelection(updated);
      setSaved(true);
    } catch {
      if (active.current && requestVersion.current === version) setSaveError(true);
    } finally {
      if (active.current && requestVersion.current === version) setSaving(false);
    }
  };
  const saveButton = <button type="button" onClick={save}
    disabled={busy || initialError || listError || !candidate}
    className="min-h-[44px] w-full rounded-xl bg-sminy-primary px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-sminy-primary-hover active:bg-sminy-primary-pressed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60">
    {saving ? 'Salvando pixel…' : 'Salvar pixel'}
  </button>;
  const control = `min-h-[44px] w-full rounded-xl border px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sminy-primary disabled:opacity-60 ${theme.input}`;

  return <section aria-labelledby="meta-pixel-title" className={`space-y-4 border-t pt-5 ${theme.border}`}>
    <div>
      <h3 id="meta-pixel-title" className={`text-base font-semibold ${theme.primaryText}`}>Pixel da clínica</h3>
      <p className={`mt-2 text-xs leading-5 ${theme.secondaryText}`}>Escolha o pixel da clínica e confira abaixo a captura e o envio de eventos.</p>
    </div>
    {initialLoading && <p role="status" className={`text-sm ${theme.secondaryText}`}>Carregando configuração do pixel…</p>}
    {initialError && <div role="alert" className={`space-y-2 text-sm ${theme.secondaryText}`}>
      <p>Não foi possível carregar a configuração do pixel.</p>
      <button type="button" className={`min-h-[44px] rounded-xl border px-3 ${theme.control}`} onClick={() => setInitialAttempt(value => value + 1)}>Tentar novamente</button>
    </div>}
    {!initialLoading && !initialError && <>
      {accounts.length === 0 ? <p className={`text-sm ${theme.secondaryText}`}>Selecione e salve uma conta de anúncios para escolher um pixel.</p> : <>
        <label className={`block text-xs font-medium ${theme.secondaryText}`} htmlFor="meta-pixel-account">Conta vinculada ao pixel</label>
        <select id="meta-pixel-account" className={control} value={accountId} disabled={saving} onChange={event => {
          ++requestVersion.current;
          candidateRef.current = '';
          setPixels([]); setPixelId(''); setSaved(false); setSaveError(false); setListLoading(true);
          setAccountId(event.target.value);
        }}>
          {accounts.map(account => <option key={account.id} value={account.id}>{account.name || account.id}</option>)}
        </select>
        <label className={`block text-xs font-medium ${theme.secondaryText}`} htmlFor="meta-pixel-choice">Pixel</label>
        <select id="meta-pixel-choice" className={control} value={pixelId} disabled={busy || listError || pixels.length === 0} onChange={event => { candidateRef.current = event.target.value; setPixelId(event.target.value); setSaved(false); setSaveError(false); }}>
          <option value="">{listLoading ? 'Carregando pixels…' : 'Selecione um pixel'}</option>
          {pixels.map(pixel => <option key={pixel.id} value={pixel.id}>{pixel.name || pixel.id}</option>)}
        </select>
        {candidate && <p className={`text-[11px] break-all ${theme.secondaryText}`}>ID: {candidate.id}</p>}
        {listLoading && <p role="status" className={`text-sm ${theme.secondaryText}`}>Consultando pixels desta conta…</p>}
        {listError && <p role="alert" className={`text-sm ${theme.secondaryText}`}>Não foi possível consultar os pixels. Verifique o acesso para tentar novamente.</p>}
        {!listLoading && !listError && pixels.length === 0 && <p className={`text-sm ${theme.secondaryText}`}>Nenhum pixel disponível nesta conta. Confira o acesso na Meta ou escolha outra conta.</p>}
      </>}
      <div className={`space-y-2 rounded-xl border p-4 text-xs ${theme.border} ${theme.secondaryText} ${isDark ? 'bg-[#14243A]' : 'bg-sminy-blue-soft'}`} aria-live="polite">
        <h4 className={`text-[13px] font-medium ${theme.primaryText}`}>Verificação de acesso</h4>
        {selection?.status === 'reselection_required' && <p>A seleção salva precisa ser confirmada. Escolha o pixel e salve novamente.</p>}
        {!listLoading && !listError && candidate ? <p className={isDark ? 'text-[#38C982]' : 'text-[#067647]'}>✓ Pixel acessível nesta conta</p> : selection?.status !== 'reselection_required' && <p>{selection?.status === 'access_unavailable'
            ? 'Não foi possível confirmar o acesso ao pixel salvo. Verifique o acesso ou reconecte a Meta.'
            : 'Selecione um pixel e verifique o acesso.'}</p>}
        <p className="text-[11px]">Selecionar um pixel não ativa o envio.</p>
      </div>
      {accounts.length > 0 && <button type="button" disabled={busy} className={`min-h-[44px] w-full rounded-xl border px-3 text-left text-sm font-medium disabled:opacity-60 ${theme.control}`} onClick={() => setListAttempt(value => value + 1)}>Verificar acesso</button>}
    </>}
    {saveError && <p role="alert" className={`text-sm ${theme.secondaryText}`}>Não foi possível salvar o pixel. Verifique o acesso antes de tentar salvar novamente.</p>}
    {saved && <p role="status" className={`text-sm ${theme.secondaryText}`}>Pixel salvo. Confira o envio abaixo.</p>}
    <MetaCrmSettings onDetailOpenChange={onDetailOpenChange} clinicId={clinicId} isDark={isDark} savedPixelKey={JSON.stringify(selection?.selection || null)} />
    {footerTarget ? createPortal(saveButton, footerTarget) : <div className="pb-[env(safe-area-inset-bottom)]">{saveButton}</div>}
  </section>;
}
